import {ValueOf} from '@wandb/weave/common/util/types';

import {View, ViewUser} from '../views/types';
import {
  DRAFT_INDICATOR,
  getNamedWorkspaceId,
  NAME_PREFIX,
  NamedWorkspaceId,
  NW_PUBLISHED_USERNAME,
  PUBLISHED_INDICATOR,
  WorkspaceTypes,
  WsRelatedId,
} from './types';

const LegacyDbNameValue: Record<string, string> = {
  // note that values here are used as the value in the views table named field,
  // so you should not change existing ones.
  user: 'workspace',
  default: 'default',
} as const;

/**
 * This is used to determine what type of workspace it is
 * based on the view table's name field
 */
export function getWorkspaceType(
  viewName: string
): ValueOf<typeof WorkspaceTypes> {
  if (viewName.startsWith(LegacyDbNameValue[WorkspaceTypes.default])) {
    return WorkspaceTypes.default;
  }
  if (viewName.startsWith(LegacyDbNameValue[WorkspaceTypes.user])) {
    return WorkspaceTypes.user;
  }
  if (viewName.startsWith(NAME_PREFIX)) {
    return WorkspaceTypes.named;
  }
  throw new Error('unknown workspace name ' + JSON.stringify({viewName}));
}

export const createViewNameForLegacyWorkspace = (
  workspaceType: keyof typeof LegacyDbNameValue,
  relatedId: WsRelatedId
) => {
  const workspaceNameSuffix = relatedId != null ? `-${relatedId}` : '';
  return LegacyDbNameValue[workspaceType] + workspaceNameSuffix;
};

// In the views table, we don't have relatedId stored anywhere, so need to include that value
// We also use the 'p'/'d' indicators to delineate published version since there's not
// a column to track that.
// We could potentially consider adding an explicit column for that if we see a need.
export const viewNameColumnForNamedWorkspace = (
  nwId: NamedWorkspaceId
): string => {
  const relatedIdSuffix = nwId.relatedId != null ? `-${nwId.relatedId}` : '';
  const templateDraftInsert =
    '-' +
    (nwId.username === NW_PUBLISHED_USERNAME
      ? PUBLISHED_INDICATOR
      : DRAFT_INDICATOR);

  return NAME_PREFIX + nwId.id + templateDraftInsert + relatedIdSuffix;
};

export const namedWorkspaceIdFromSavedView = (
  viewName: string,
  viewUser: ViewUser
): NamedWorkspaceId => {
  // an example viewName here will look like:
  // nw-{id}-{type}
  // OR
  // nw-{id}-{type}-{relId}
  const s = viewName.split('-');
  if (s.length < 3 || s.length > 4) {
    throw new Error(`invalid view.name for named workspace: ${viewName}`);
  }
  // s[0] is 'nw'
  const id = s[1];
  const username =
    s[2] === PUBLISHED_INDICATOR ? NW_PUBLISHED_USERNAME : viewUser.username;

  return getNamedWorkspaceId(
    id,
    username,
    s[3] /* relies on out of bound index returning undefined */
  );
};

export const findView = (views: View[], nwId: NamedWorkspaceId) => {
  return views.find(v => v.name === viewNameColumnForNamedWorkspace(nwId));
};
